/**
 * 1.6 – AI Hỗ trợ Soạn thảo Văn bản Tố tụng
 * Layout: Editor TipTap | Panel AI tự điền (Kính gửi, Về việc) + gợi ý động. Chọn biểu mẫu ở thanh trên.
 * Topbar: biểu mẫu, Xem trước, Lưu & In.
 */
import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Stack,
  Typography,
  Paper,
  Link,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  IconButton,
  Collapse,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  GlobalStyles,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import {
  AutoAwesome,
  Visibility,
  Save,
  ChevronRight,
  Refresh,
  Assignment,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  Print,
} from '@mui/icons-material'
import { API_BASE_URL } from '../../config/api'
import { renderAsync } from 'docx-preview'
import LegalDraftTiptapEditor from '../../components/workspace/LegalDraftTiptapEditor'

type HighlightField = { key: string; label: string; token: string }

const THU_LY_HIGHLIGHT_FIELDS: HighlightField[] = [
  { key: 'KHU_VUC', label: 'Khu vực', token: 'KHU VỰC X' },
  { key: 'THANH_PHO', label: 'Thành phố', token: 'THÀNH PHỐ HỒ CHÍ MINH' },
  { key: 'SO_TB', label: 'Số thông báo', token: '10/TB-TLVA' },
  { key: 'NGAY_TP', label: 'Địa danh, ngày tháng', token: 'TP.HCM, ngày 10 tháng 4 năm 2026' },
  { key: 'VKS', label: 'Viện kiểm sát (Kính gửi)', token: 'Viện kiểm sát nhân dân khu vực X – Thành Phố Hồ Chí Minh' },
  { key: 'NGUYEN_DON', label: 'Nguyên đơn (họ tên)', token: 'Ông Nguyễn Văn Vinh' },
  { key: 'ND_DIA_CHI', label: 'Nguyên đơn (địa chỉ)', token: '100/23 Nguyễn Văn Văn, phường XXX, Thành Phố Hồ Chí Minh' },
  { key: 'ND_SDT', label: 'Nguyên đơn (số điện thoại)', token: '090xxx' },
  { key: 'BI_DON', label: 'Bị đơn (họ tên)', token: 'Bà Trần Thị Hường' },
  { key: 'BD_DIA_CHI', label: 'Bị đơn (địa chỉ)', token: '200 Nguyễn Hữu Thọ, phường YYY, Thành Phố Hồ Chí Minh' },
  { key: 'BD_SDT', label: 'Bị đơn (số điện thoại)', token: '091xxx' },
  { key: 'NGAY_THU_LY', label: 'Ngày thụ lý (trong nội dung)', token: 'Ngày 10 tháng 4 năm 2026' },
  { key: 'SO_THU_LY', label: 'Số thụ lý', token: '10/2026/TLST- HNGĐ' },
  { key: 'VEC_VIEC', label: 'Về việc', token: 'Ly hôn' },
  { key: 'CON_CHUNG', label: 'Con chung (mục 2)', token: 'Yêu cầu trực tiếp nuôi dưỡng con chung là cháu Nguyễn Văn C (sinh năm 2020) và không yêu cầu cấp dưỡng.' },
  { key: 'TAI_SAN', label: 'Tài sản/nợ chung (mục 3)', token: 'Tài sản chung, nợ chung: Tự thỏa thuận.' },
  { key: 'CCCD', label: 'Tài liệu (bản sao)', token: 'CCCD, Giấy xác nhận cư trú, Giấy khai sinh' },
  { key: 'DK_KET_HON', label: 'Tài liệu (bản chính)', token: 'Giấy đăng ký kết hôn' },
  { key: 'NOI_NHAN_VKS', label: 'Nơi nhận: VKS', token: 'VKS kv X -TP.HCM' },
  { key: 'NOI_NHAN_DUONG_SU', label: 'Nơi nhận: Đương sự', token: 'Đương sự' },
  { key: 'THAM_PHAN', label: 'Thẩm phán (ký tên)', token: 'Trần Văn Bình' },
]

function buildHighlightDefaults(): Record<string, string> {
  const out: Record<string, string> = {}
  for (const f of THU_LY_HIGHLIGHT_FIELDS) out[f.key] = f.token
  return out
}

type PartyFields = {
  xungHo: string
  hoTen: string
  diaChi: string
  dienThoai: string
}

type ThuLyAiFields = {
  khuVuc: string
  thanhPho: string
  soThongBao: string
  diaDanhNgayThang: string
  nguyenDon: PartyFields
  biDon: PartyFields
  ngayThuLy: string
  soVuAn: string
  veViec: string
  nguoiKhoiKien: string
  diaChiNguoiKhoiKien: string
  dienThoaiNguoiKhoiKien: string
  vanDeYeuCau: string[]
  taiLieuChungCu: string[]
  noiNhan: string[]
  thamPhan: string
}

function buildThuLyAiDefaults(): ThuLyAiFields {
  return {
    khuVuc: 'KHU VỰC X',
    thanhPho: 'THÀNH PHỐ HỒ CHÍ MINH',
    soThongBao: '10/TB-TLVA',
    diaDanhNgayThang: 'TP.HCM, ngày 10 tháng 4 năm 2026',
    nguyenDon: {
      xungHo: 'Ông',
      hoTen: 'Nguyễn Văn Vinh',
      diaChi: '100/23 Nguyễn Văn Văn, phường XXX, Thành Phố Hồ Chí Minh',
      dienThoai: '090xxx',
    },
    biDon: {
      xungHo: 'Bà',
      hoTen: 'Trần Thị Hường',
      diaChi: '200 Nguyễn Hữu Thọ, phường YYY, Thành Phố Hồ Chí Minh',
      dienThoai: '091xxx',
    },
    ngayThuLy: 'Ngày 10 tháng 4 năm 2026',
    /** Khớp đúng file Demo.docx: `10/2026/TLST-HNGĐ` (không có khoảng trước HNGĐ) */
    soVuAn: '10/2026/TLST-HNGĐ',
    veViec: 'Ly hôn',
    nguoiKhoiKien: 'Ông Nguyễn Văn Vinh',
    diaChiNguoiKhoiKien: '100/23 Nguyễn Văn Văn, phường XXX, Thành Phố Hồ Chí Minh',
    dienThoaiNguoiKhoiKien: '090xxx',
    vanDeYeuCau: [
      'Ly hôn với bà Trần Thị Hường.',
      'Yêu cầu trực tiếp nuôi dưỡng con chung là cháu Nguyễn Văn C (sinh năm 2020) và không yêu cầu cấp dưỡng.',
      'Tài sản chung, nợ chung: Tự thỏa thuận.',
    ],
    taiLieuChungCu: ['CCCD, Giấy xác nhận cư trú, Giấy khai sinh', 'Giấy đăng ký kết hôn'],
    noiNhan: ['VKS kv X -TP.HCM', 'Đương sự', 'Lưu hồ sơ vụ án'],
    thamPhan: 'Trần Văn Bình',
  }
}

/**
 * Thay chuỗi trong DOM sau docx-preview: Word thường tách một cụm thành nhiều run → nhiều text node liền kề.
 * replace theo từng node không khớp chuỗi dài; ghép nội dung text (bỏ qua span highlight) rồi Range.delete/insert.
 */
function applyThuLyHighlightDocx(root: HTMLElement, fields: ThuLyAiFields) {
  /** Giữ font/cỡ/in nghiêng của run gốc (docx-preview); chỉ thêm nền vàng, không ghi đè Times New Roman cố định. */
  const highlightWrap = (text: string, styleSource: Element | null) => {
    const span = document.createElement('span')
    span.setAttribute('data-ai-highlight', '1')
    span.style.backgroundColor = 'rgba(255, 235, 59, 0.55)'
    span.style.padding = '0 2px'
    span.style.borderRadius = '2px'
    if (styleSource) {
      const cs = getComputedStyle(styleSource)
      span.style.fontFamily = cs.fontFamily
      span.style.fontSize = cs.fontSize
      span.style.fontStyle = cs.fontStyle
      span.style.fontWeight = cs.fontWeight
      span.style.fontVariant = cs.fontVariant
      span.style.lineHeight = cs.lineHeight
      span.style.letterSpacing = cs.letterSpacing
      span.style.textDecoration = cs.textDecoration
      span.style.color = cs.color
      span.style.verticalAlign = cs.verticalAlign
    }
    span.textContent = text
    return span
  }

  const replaceAllCrossText = (search: string, replacement: string) => {
    if (!search) return
    if (replacement === search) return
    let guard = 0
    while (guard++ < 400) {
      const nodes: Text[] = []
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
      while (walker.nextNode()) {
        const n = walker.currentNode as Text
        if (n.parentElement?.closest('[data-ai-highlight="1"]')) continue
        nodes.push(n)
      }
      const full = nodes.map((n) => n.nodeValue || '').join('')
      const i = full.indexOf(search)
      if (i < 0) return
      const L = search.length
      const charMap: { node: Text; offset: number }[] = []
      for (const n of nodes) {
        const v = n.nodeValue || ''
        for (let k = 0; k < v.length; k++) charMap.push({ node: n, offset: k })
      }
      if (i + L > charMap.length) return
      const start = charMap[i]
      const end = charMap[i + L - 1]
      const styleSource = start.node.parentElement
      const range = document.createRange()
      range.setStart(start.node, start.offset)
      range.setEnd(end.node, end.offset + 1)
      range.deleteContents()
      range.insertNode(highlightWrap(replacement, styleSource))
    }
  }

  const replaceAny = (searches: string[], replacement: string) => {
    for (const s of searches) {
      if (!s) continue
      replaceAllCrossText(s, replacement)
    }
  }

  const khuShort = fields.khuVuc.replace(/^KHU\s+VỰC\s+/i, '').trim() || 'X'
  const vksFull = `Viện kiểm sát nhân dân khu vực ${khuShort} – ${fields.thanhPho}`.replace(/\s+/g, ' ').trim()
  const toaAnLine = `Tòa án nhân dân khu vực ${khuShort} – ${fields.thanhPho}`.replace(/\s+/g, ' ').trim()
  const nguyenDonFull = `${fields.nguyenDon.xungHo} ${fields.nguyenDon.hoTen}`.replace(/\s+/g, ' ').trim()
  const biDonFull = `${fields.biDon.xungHo} ${fields.biDon.hoTen}`.replace(/\s+/g, ' ').trim()
  const veViecEscaped = (fields.veViec || 'Ly hôn').trim() || 'Ly hôn'

  replaceAllCrossText('KHU VỰC X', fields.khuVuc)
  replaceAllCrossText('THÀNH PHỐ HỒ CHÍ MINH', fields.thanhPho)
  replaceAllCrossText('10/TB-TLVA', fields.soThongBao)
  replaceAllCrossText('TP.HCM, ngày 10 tháng 4 năm 2026', fields.diaDanhNgayThang)

  replaceAllCrossText('Viện kiểm sát nhân dân khu vực X – Thành Phố Hồ Chí Minh', vksFull)
  replaceAllCrossText('Viện kiểm sát nhân dân khu vực X – Thành Phố Hồ Chí Minh.', `${vksFull}.`)

  replaceAllCrossText('Tòa án nhân dân khu vực X – Thành Phố Hồ Chí Minh', toaAnLine)

  /**
   * Không dùng replace riêng chuỗi "Ngày 10 tháng 4 năm 2026" — trùng cụm ngày trong dòng địa danh
   * (vd. "Hà Nội, ngày 10 tháng 4 năm 2026") sẽ làm vỡ dòng sau khi highlight. Chỉ thay cả đoạn mở đầu câu thụ lý.
   */
  replaceAllCrossText(
    `Ngày 10 tháng 4 năm 2026, ${toaAnLine} đã thụ lý`,
    `${fields.ngayThuLy}, ${toaAnLine} đã thụ lý`,
  )
  replaceAllCrossText(
    `Ngày 10 tháng 4 năm 2026, ${toaAnLine} đã thụ lý vụ án`,
    `${fields.ngayThuLy}, ${toaAnLine} đã thụ lý vụ án`,
  )
  /**
   * Fallback mục 8: nếu DOCX mẫu khác chữ/khoảng trắng so với chuỗi trên, vẫn thay cụm ngày mở đầu câu thụ lý.
   * replaceAllCrossText bỏ qua vùng highlight → không đụng dòng địa danh (đã highlight ở bước TP.HCM…).
   */
  replaceAllCrossText('Ngày 10 tháng 4 năm 2026', fields.ngayThuLy)

  replaceAllCrossText('Ông Nguyễn Văn Vinh', nguyenDonFull)
  replaceAllCrossText('100/23 Nguyễn Văn Văn, phường XXX, Thành Phố Hồ Chí Minh', fields.nguyenDon.diaChi)
  replaceAllCrossText('090xxx', fields.nguyenDon.dienThoai)

  replaceAllCrossText('Bà Trần Thị Hường', biDonFull)
  replaceAllCrossText('200 Nguyễn Hữu Thọ, phường YYY, Thành Phố Hồ Chí Minh', fields.biDon.diaChi)
  replaceAllCrossText('091xxx', fields.biDon.dienThoai)

  replaceAny(['10/2026/TLST-HNGĐ', '10/2026/TLST- HNGĐ'], fields.soVuAn)

  if (fields.vanDeYeuCau[0]) replaceAny(['Ly hôn với bà Trần Thị Hường.', 'Ly hôn với Bà Trần Thị Hường.'], fields.vanDeYeuCau[0])
  if (fields.vanDeYeuCau[1])
    replaceAllCrossText(
      'Yêu cầu trực tiếp nuôi dưỡng con chung là cháu Nguyễn Văn C (sinh năm 2020) và không yêu cầu cấp dưỡng.',
      fields.vanDeYeuCau[1],
    )
  if (fields.vanDeYeuCau[2]) replaceAllCrossText('Tài sản chung, nợ chung: Tự thỏa thuận.', fields.vanDeYeuCau[2])

  replaceAny([`về việc “Ly hôn”`, 'về việc "Ly hôn"'], `về việc “${veViecEscaped}”`)
  replaceAllCrossText('Ly hôn', veViecEscaped)

  replaceAllCrossText('Theo đơn khởi kiện của Ông Nguyễn Văn Vinh', `Theo đơn khởi kiện của ${fields.nguoiKhoiKien}`)
  replaceAny(
    [
      'Địa chỉ: 100/23 Nguyễn Văn Văn, phường XXX, Thành Phố Hồ Chí Minh',
      'Địa chỉ: 100/23 Nguyễn Văn Văn, phường XXX, Thành Phố Hồ Chí Minh ',
    ],
    `Địa chỉ: ${fields.diaChiNguoiKhoiKien}`,
  )
  replaceAny(['Số điện thoại: 090xxx', 'Số điện thoại:090xxx'], `Số điện thoại: ${fields.dienThoaiNguoiKhoiKien}`)

  if (fields.taiLieuChungCu[0]) replaceAllCrossText('CCCD, Giấy xác nhận cư trú, Giấy khai sinh', fields.taiLieuChungCu[0])
  if (fields.taiLieuChungCu[1]) replaceAllCrossText('Giấy đăng ký kết hôn', fields.taiLieuChungCu[1])

  replaceAllCrossText('VKS kv X -TP.HCM', fields.noiNhan[0] || 'VKS kv X -TP.HCM')
  replaceAllCrossText('Đương sự', fields.noiNhan[1] || 'Đương sự')
  replaceAllCrossText('Trần Văn Bình', fields.thamPhan)

  applyTieuNguFontSize(root)
  stripHiddenElementsBeforeDiaDanh(root, fields.diaDanhNgayThang)
  applyDiaDanhDateNudge(root, fields.diaDanhNgayThang)
}

function normDiaDanhPlain(s: string): string {
  return s.replace(/[\u200B-\u200D\uFEFF]/g, '').replace(/\s+/g, ' ').trim()
}

/** Khối chứa dòng địa danh (kể cả text tách nhiều run). Ưu tiên khớp chính xác, sau đó khớp theo đoạn (docx có thể lệch khoảng trắng / user đổi form). */
function findDiaDanhBlock(root: HTMLElement, diaDanh: string): HTMLElement | null {
  const want = normDiaDanhPlain(diaDanh)
  if (!want) return null
  const all = root.querySelectorAll('p, div, section, article, td, li')
  const hits: HTMLElement[] = []
  for (const el of all) {
    const h = el as HTMLElement
    if (normDiaDanhPlain(h.textContent ?? '') === want) hits.push(h)
  }
  for (const h of hits) {
    const isAncestorOfOther = hits.some((o) => o !== h && h.contains(o))
    if (!isAncestorOfOther) return h
  }
  if (hits[0]) return hits[0]

  /** Fallback: đoạn một dòng (thường trong ô bảng) chứa đủ chuỗi địa danh do user nhập — tránh khớp đoạn dài trong thân văn. */
  const ps = root.querySelectorAll('p')
  const shortLine = (s: string) => normDiaDanhPlain(s).length <= 220
  for (const el of ps) {
    const h = el as HTMLElement
    const got = normDiaDanhPlain(h.textContent ?? '')
    if (!shortLine(got)) continue
    if (want.length >= 8 && got.includes(want)) return h
  }
  return null
}

function removeLeadingHiddenFromBlock(block: HTMLElement) {
  while (block.firstChild && shouldRemoveLeadingNode(block.firstChild)) {
    block.removeChild(block.firstChild)
  }
}

/** Word/docx-preview đôi khi chèn span/tab/field ẩn trước dòng địa danh → lệch căn. Gỡ node rỗng/ẩn đứng trước text khớp. */
function stripHiddenElementsBeforeDiaDanh(root: HTMLElement, diaDanh: string) {
  const target = diaDanh.trim()
  if (!target) return

  const block = findDiaDanhBlock(root, diaDanh)
  if (block) {
    removeLeadingHiddenFromBlock(block)
    return
  }

  const norm = (s: string) => s.replace(/[\u200B-\u200D\uFEFF]/g, '').trim()
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const texts: Text[] = []
  while (walker.nextNode()) texts.push(walker.currentNode as Text)

  for (const n of texts) {
    const raw = n.nodeValue ?? ''
    if (norm(raw) !== target) continue

    const trimmedStart = raw.replace(/^[\u200B-\u200D\uFEFF\s]+/, '')
    if (trimmedStart !== raw) {
      n.nodeValue = trimmedStart
    }

    removePrecedingHiddenPeersBeforeNode(n, root)
  }
}

function shouldRemoveLeadingNode(node: ChildNode): boolean {
  if (node.nodeType === Node.COMMENT_NODE) return true
  if (node.nodeType === Node.TEXT_NODE) {
    const t = (node as Text).nodeValue?.replace(/[\u200B-\u200D\uFEFF\s]/g, '') ?? ''
    return t.length === 0
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return false
  const el = node as HTMLElement
  try {
    const cs = getComputedStyle(el)
    if (cs.display === 'none' || cs.visibility === 'hidden') return true
    if (parseFloat(cs.opacity) === 0) return true
    if (parseFloat(cs.fontSize) === 0) return true
  } catch {
    /* ignore */
  }
  if (el.getAttribute('aria-hidden') === 'true') return true
  if (el.hasAttribute('hidden')) return true
  const txt = el.textContent?.replace(/[\u200B-\u200D\uFEFF]/g, '').trim() ?? ''
  if (!txt && el.children.length === 0) return true
  return false
}

/** Lần lên từ text node tới root: xóa previousSibling là ẩn/rỗng (Word thường để span ẩn sát trước TP.HCM). */
function removePrecedingHiddenPeersBeforeNode(textNode: Text, root: HTMLElement) {
  let node: ChildNode | null = textNode
  let guard = 0
  while (node && node !== root && guard++ < 24) {
    let prev = node.previousSibling
    while (prev) {
      if (!shouldRemoveLeadingNode(prev)) break
      const rm = prev
      prev = prev.previousSibling
      rm.parentNode?.removeChild(rm)
    }
    const parent = node.parentNode
    if (!parent || parent === root) break
    node = parent as unknown as ChildNode
  }
}

/** Ghi đè canh lề Word (thường canh phải ô cột) để dòng địa danh căn giữa cùng trục với tiêu ngữ. */
function applyDiaDanhAlignmentStyles(block: HTMLElement, root: HTMLElement) {
  const markOn = (block.closest('p') || block.closest('td') || block) as HTMLElement
  if (markOn.getAttribute('data-dia-danh-nudge') === '1') return
  let el: HTMLElement | null = block
  while (el && el !== root) {
    const tag = el.tagName
    if (tag === 'P' || tag === 'DIV') {
      el.style.setProperty('text-align', 'center', 'important')
      el.style.setProperty('width', '100%', 'important')
      el.style.setProperty('box-sizing', 'border-box', 'important')
      el.style.setProperty('padding-left', '0', 'important')
      el.style.setProperty('padding-right', '0', 'important')
      el.style.setProperty('margin-left', '0', 'important')
      el.style.setProperty('margin-right', '0', 'important')
      el.style.setProperty('font-style', 'normal', 'important')
    }
    /** Word thường đặt text-align:right trên td → phải ghi đè cả ô chứa dòng địa danh. */
    if (tag === 'TD') {
      el.style.setProperty('text-align', 'center', 'important')
      el.style.setProperty('vertical-align', 'top', 'important')
    }
    el = el.parentElement
  }
  markOn.setAttribute('data-dia-danh-nudge', '1')
}

/** Dòng địa danh / thời gian: căn giữa cột (cùng trục với tiêu ngữ). */
function applyDiaDanhDateNudge(root: HTMLElement, diaDanh: string) {
  const target = diaDanh.trim()
  if (!target) return

  const block = findDiaDanhBlock(root, diaDanh)
  if (block) {
    applyDiaDanhAlignmentStyles(block, root)
    return
  }

  const norm = (s: string) => s.replace(/[\u200B-\u200D\uFEFF]/g, '').trim()
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const texts: Text[] = []
  while (walker.nextNode()) texts.push(walker.currentNode as Text)
  for (const n of texts) {
    if (norm(n.nodeValue ?? '') !== target) continue
    let el: HTMLElement | null = n.parentElement
    while (el && el !== root) {
      const tag = el.tagName
      if (tag === 'P' || tag === 'DIV' || tag === 'SECTION' || tag === 'ARTICLE') {
        applyDiaDanhAlignmentStyles(el, root)
        return
      }
      el = el.parentElement
    }
    const anchor = n.parentElement?.closest('p') ?? n.parentElement?.closest('td')
    if (anchor) applyDiaDanhAlignmentStyles(anchor as HTMLElement, root)
    return
  }
}

/** Tiêu ngữ (CỘNG HÒA…, Độc lập…) — cố định 13px, không ghi đè toàn văn bản. */
function applyTieuNguFontSize(root: HTMLElement) {
  const normalize = (s: string) => s.replace(/\s+/g, ' ').trim()
  const lineMatchesTieuNgu = (raw: string) => {
    const v = normalize(raw)
    if (v === 'CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM') return true
    if (v === 'Độc lập - Tự do - Hạnh phúc' || v === 'Độc lập – Tự do – Hạnh phúc') return true
    return false
  }
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const texts: Text[] = []
  while (walker.nextNode()) texts.push(walker.currentNode as Text)
  for (const n of texts) {
    if (n.parentElement?.closest('[data-ai-highlight="1"]')) continue
    if (!lineMatchesTieuNgu(n.nodeValue ?? '')) continue
    const el = n.parentElement
    if (!el || el.getAttribute('data-tieu-ngu-font') === '1') continue
    el.style.fontSize = '14px'
    el.setAttribute('data-tieu-ngu-font', '1')
  }
}

function highlightPhrases(text: string, phrases: string[]) {
  if (!phrases.length) return text
  const sorted = [...phrases].filter(Boolean).sort((a, b) => b.length - a.length)
  let out: (string | { t: string })[] = [text]
  for (const ph of sorted) {
    const next: (string | { t: string })[] = []
    for (const part of out) {
      if (typeof part !== 'string') {
        next.push(part)
        continue
      }
      const s = part
      const idx = s.indexOf(ph)
      if (idx < 0) {
        next.push(s)
        continue
      }
      // split all occurrences
      let cursor = 0
      while (true) {
        const i = s.indexOf(ph, cursor)
        if (i < 0) {
          next.push(s.slice(cursor))
          break
        }
        if (i > cursor) next.push(s.slice(cursor, i))
        next.push({ t: ph })
        cursor = i + ph.length
      }
    }
    out = next
  }
  return out
}

/** Header cố định theo format hệ thống (ảnh mẫu); phần thân lấy từ docContent từ "Kính gửi:". */
function AdminNoticePreview({ text, suggestionPhrases }: { text: string; suggestionPhrases: string[] }) {
  const bodyStart = text.indexOf('Kính gửi:')
  const body = (bodyStart >= 0 ? text.slice(bodyStart) : text).trimEnd()

  const renderHighlighted = (chunk: string) => {
    const parts = highlightPhrases(chunk, suggestionPhrases)
    if (!Array.isArray(parts)) return chunk
    return parts.map((p, i) =>
      typeof p === 'string' ? (
        <span key={i}>{p}</span>
      ) : (
        <Box
          key={i}
          component="span"
          className="ai-suggestion"
          sx={{
            backgroundColor: 'rgba(255, 235, 59, 0.35)',
            padding: '0 2px',
            borderRadius: 1,
          }}
        >
          {p.t}
        </Box>
      ),
    )
  }

  const bodyLines = body.split('\n')
  const afterKinhGui = bodyLines[0] === 'Kính gửi:' ? bodyLines.slice(1) : bodyLines
  const restLines = afterKinhGui

  return (
    <Box sx={{ fontFamily: 'Times New Roman, serif', fontSize: 13, lineHeight: 1.8 }}>
      <Stack direction="row" spacing={4} alignItems="flex-start" sx={{ mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, textAlign: 'center', textTransform: 'uppercase', lineHeight: 1.35 }}
          >
            TÒA ÁN NHÂN DÂN KHU VỰC X
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, textAlign: 'center', textTransform: 'uppercase', mt: 0.1, lineHeight: 1.35 }}
          >
            THÀNH PHỐ HỒ CHÍ MINH
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.85, textAlign: 'left', lineHeight: 1.35 }}>
            Số: 10/TB-TLVA
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, textAlign: 'center', textTransform: 'uppercase', lineHeight: 1.35, fontSize: 13 }}
          >
            CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, textAlign: 'center', mt: 0.1, lineHeight: 1.35, fontSize: 13 }}
          >
            Độc lập - Tự do - Hạnh phúc
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontStyle: 'normal', textAlign: 'center', mt: 0.85, lineHeight: 1.35, width: '100%' }}
          >
            TP.HCM, ngày 10 tháng 4 năm 2026
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>
          THÔNG BÁO
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 800, textTransform: 'uppercase', mt: 0.5 }}>
          VỀ VIỆC THỤ LÝ VỤ ÁN
        </Typography>
      </Box>

      <Box sx={{ textAlign: 'left' }}>
        <Typography variant="body2" sx={{ fontWeight: 400, fontStyle: 'normal', mb: 0.75 }}>
          Kính gửi:
        </Typography>
        {restLines.map((line, idx) => {
          if (!line.trim()) {
            return <Box key={`sp-${idx}`} sx={{ height: 10 }} />
          }
          if (/^Nơi nhận:/.test(line.trim()) && line.includes('THẨM PHÁN')) {
            return (
              <Stack key={`nh-${idx}`} direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="body2" component="span" sx={{ textAlign: 'justify', flex: 1 }}>
                  Nơi nhận:
                </Typography>
                <Typography variant="body2" component="span" sx={{ fontWeight: 700, flexShrink: 0 }}>
                  THẨM PHÁN
                </Typography>
              </Stack>
            )
          }
          if (line.startsWith('- ') && /Trần Văn Bình\s*$/.test(line) && line.includes('Đương sự')) {
            const segs = line.split(/\s{2,}/).filter(Boolean)
            const left = segs[0] ?? line
            return (
              <Stack key={`tp-${idx}`} direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="body2" component="span" sx={{ textAlign: 'justify', flex: 1 }}>
                  {renderHighlighted(left)}
                </Typography>
                <Typography variant="body2" component="span" sx={{ fontWeight: 700, flexShrink: 0 }}>
                  {renderHighlighted('Trần Văn Bình')}
                </Typography>
              </Stack>
            )
          }
          if (line.startsWith('- ')) {
            return (
              <Typography
                key={`b-${idx}`}
                variant="body2"
                component="div"
                sx={{ pl: 4, pr: 0, mb: 0.5, textAlign: 'justify', textIndent: 0 }}
              >
                {renderHighlighted(line)}
              </Typography>
            )
          }
          if (/^\d+\.\s/.test(line)) {
            return (
              <Typography
                key={`n-${idx}`}
                variant="body2"
                component="div"
                sx={{
                  pl: '2em',
                  textIndent: '-1.5em',
                  ml: '1.5em',
                  mr: 0,
                  mb: 0.5,
                  textAlign: 'justify',
                }}
              >
                {renderHighlighted(line)}
              </Typography>
            )
          }
          const m = line.match(/^Theo đơn khởi kiện của\s+(.+)$/)
          if (m) {
            return (
              <Typography key={`t-${idx}`} variant="body2" component="div" sx={{ mb: 0.75, textAlign: 'justify' }}>
                Theo đơn khởi kiện của <Box component="span" sx={{ fontWeight: 700 }}>{renderHighlighted(m[1].trim())}</Box>
              </Typography>
            )
          }
          const indent = line.startsWith('Ngày ')
          return (
            <Typography
              key={`p-${idx}`}
              variant="body2"
              component="div"
              sx={{
                mb: 0.75,
                textAlign: 'justify',
                textIndent: indent ? '2em' : 0,
              }}
            >
              {renderHighlighted(line)}
            </Typography>
          )
        })}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Các trường AI điền có nền vàng nhạt.
      </Typography>
    </Box>
  )
}

const QUICK_TEMPLATES = [
  'Thông báo thụ lý',
  'Giấy triệu tập',
  'Quyết định đưa vụ án ra xét xử',
  'Biên bản hòa giải',
  'Bản án sơ thẩm',
]

/** Nội dung mẫu điền sẵn theo từng biểu mẫu. */
const TEMPLATE_CONTENT: Record<string, string> = {
  'Thông báo thụ lý': `Kính gửi:
- Viện kiểm sát nhân dân khu vực X – Thành Phố Hồ Chí Minh.
- Ông Nguyễn Văn Vinh; Địa chỉ: 100/23 Nguyễn Văn Văn, phường XXX, Thành Phố Hồ Chí Minh; Số điện thoại: 090xxx; là nguyên đơn.
- Bà Trần Thị Hường; Địa chỉ: 200 Nguyễn Hữu Thọ, phường YYY, Thành Phố Hồ Chí Minh; Số điện thoại: 091xxx; là bị đơn.

Ngày 10 tháng 4 năm 2026, Tòa án nhân dân khu vực X – Thành Phố Hồ Chí Minh đã thụ lý vụ án hôn nhân gia đình số: 10/2026/TLST- HNGĐ về việc " Ly hôn "

Theo đơn khởi kiện của Ông Nguyễn Văn Vinh

Địa chỉ: 100/23 Nguyễn Văn Văn, phường XXX, Thành Phố Hồ Chí Minh

Số điện thoại: 090xxx

Những vấn đề cụ thể người khởi kiện yêu cầu Toà án giải quyết bao gồm:
1. Ly hôn với bà Trần Thị Hường.
2. Yêu cầu trực tiếp nuôi dưỡng con chung là cháu Nguyễn Văn C (sinh năm 2020) và không yêu cầu cấp dưỡng.
3. Tài sản chung, nợ chung: Tự thỏa thuận.

Kèm theo đơn khởi kiện, người khởi kiện đã nộp các tài liệu, chứng cứ sau đây:
1. Bản sao có chứng thực: CCCD, Giấy xác nhận cư trú, Giấy khai sinh
2. Bản chính: Giấy đăng ký kết hôn.

Căn cứ vào Điều 196 và Điều 199 của Bộ luật tố tụng dân sự, Toà án nhân dân thông báo cho Viện kiểm sát nhân dân khu vực X – Thành Phố Hồ Chí Minh và các đương sự có tên nêu trên được biết.

Trong thời hạn 15 ngày, kể từ ngày nhận được Thông báo này, bị đơn, người có quyền lợi, nghĩa vụ liên quan phải nộp cho Toà án văn bản ghi ý kiến của mình đối với yêu cầu của nguyên đơn và các tài liệu, chứng cứ kèm theo, yêu cầu phản tố, yêu cầu độc lập (nếu có). Trường hợp cần gia hạn, thì phải có đơn xin gia hạn gửi cho Toà án nêu rõ lý do để Toà án xem xét. Hết thời hạn này mà người được thông báo không nộp cho cho Tòa án văn bản về ý kiến của mình đối với yêu cầu khởi kiện của người khởi kiện thì Toà án căn cứ vào các tài liệu, chứng cứ có trong hồ sơ để giải quyết vụ án theo quy định của pháp luật.

Nơi nhận:                                            THẨM PHÁN
- VKS kv X -TP.HCM;                                 
- Đương sự;                                         Trần Văn Bình
- Lưu hồ sơ vụ án.`,
  'Giấy triệu tập': `CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM
Độc lập – Tự do – Hạnh phúc

———————————

Số: 01/2026/GT
Ngày: 15/03/2026

GIẤY TRIỆU TẬP
(Tham gia phiên họp kiểm tra chứng cứ)

Kính gửi:
- Nguyên đơn: Lê Hoàng Nam
- Bị đơn: Nguyễn Thị Mai Linh

Vụ án: HN-GD-2026-001 – Tranh chấp hôn nhân và gia đình (ly hôn).

Tòa án nhân dân phường Bến Thành, TP.HCM triệu tập các bên tham gia phiên họp kiểm tra chứng cứ vào:

  Thời gian: 08 giờ 00, ngày 25 tháng 03 năm 2026
  Địa điểm: Phòng xử án số 3 – Tòa án nhân dân phường Bến Thành, TP.HCM (hoặc tham gia trực tuyến theo link được gửi qua hệ thống).

Các bên mang theo: CMND/CCCD (bản chính), Giấy chứng nhận kết hôn, các tài liệu chứng cứ đã nộp (bản gốc nếu cần đối chiếu).

Nếu vắng mặt không có lý do chính đáng, Tòa án có thể tiến hành xét xử vắng mặt theo quy định pháp luật.

Nơi nhận:
- Nguyên đơn, Bị đơn (đã nêu);
- Lưu hồ sơ vụ án.`,
  'Quyết định đưa vụ án ra xét xử': `CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM
Độc lập – Tự do – Hạnh phúc

———————————

Số: 01/2026/QĐ-ĐVARTX
Ngày: 20/02/2026

QUYẾT ĐỊNH
Đưa vụ án dân sự ra xét xử

Thẩm phán: Thẩm phán Trần Văn Bình – Tòa án nhân dân phường Bến Thành, TP.HCM.

Căn cứ vào các Điều 203, 204 Bộ luật Tố tụng dân sự;

Xét hồ sơ vụ án HN-GD-2026-001 – Tranh chấp hôn nhân và gia đình (Nguyên đơn: Lê Hoàng Nam; Bị đơn: Nguyễn Thị Mai Linh). Sau khi thụ lý, Tòa án đã tiến hành hòa giải nhưng các bên không thỏa thuận được. Hồ sơ đã đủ điều kiện đưa vụ án ra xét xử.

QUYẾT ĐỊNH:

Đưa vụ án dân sự số HN-GD-2026-001 ra xét xử sơ thẩm tại Tòa án nhân dân phường Bến Thành, TP.HCM. Thời gian và địa điểm phiên tòa sẽ được thông báo bằng Giấy triệu tập riêng.

Quyết định có hiệu lực kể từ ngày ký.`,
  'Biên bản hòa giải': `CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM
Độc lập – Tự do – Hạnh phúc

———————————

BIÊN BẢN HÒA GIẢI
(Vụ án dân sự)

Số: 01/2026/BB-HG
Ngày: 18/02/2026 – Tại Phòng hòa giải, Tòa án nhân dân phường Bến Thành, TP.HCM.

Thành phần:
- Thẩm phán chủ trì: Thẩm phán Trần Văn Bình
- Thư ký: Thư ký Nguyễn Thị Hương
- Nguyên đơn: Lê Hoàng Nam (có mặt)
- Bị đơn: Nguyễn Thị Mai Linh (có mặt)

Vụ án: HN-GD-2026-001 – Yêu cầu giải quyết ly hôn.

Nội dung hòa giải: Thẩm phán đã giải thích quyền, nghĩa vụ và thủ tục hòa giải. Hai bên trình bày ý kiến. Tòa án đề xuất phương án hòa giải nhưng các bên không đạt được thỏa thuận.

Kết quả: Hòa giải không thành. Tòa án lập biên bản này, các bên ký xác nhận và chuyển hồ sơ sang giai đoạn chuẩn bị xét xử.

Biên bản được lập xong, đọc lại cho các bên cùng nghe, các bên đồng ý và ký tên dưới đây.`,
  'Bản án sơ thẩm': `CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM
Độc lập – Tự do – Hạnh phúc

———————————

BẢN ÁN DÂN SỰ SƠ THẨM
(Số: 01/2026/BA-DS-ST)

Vụ án: Tranh chấp ly hôn – HN-GD-2026-001

Nguyên đơn: Lê Hoàng Nam.
Bị đơn: Nguyễn Thị Mai Linh.

Ngày mở phiên: 25/03/2026. Thẩm phán chủ trì: Thẩm phán Trần Văn Bình.

Căn cứ vào biên bản phiên tòa, chứng cứ và lời khai các bên; Căn cứ Điều 51, 56 Luật Hôn nhân và gia đình 2014; Điều 147, 203 Bộ luật Tố tụng dân sự 2015;

XÉT XỬ:

[Phần nhận định của Tòa án – Thẩm phán soạn dựa trên chứng cứ và áp dụng pháp luật. Gợi ý: Tra cứu điều luật, án lệ tại Trợ lý pháp luật để tham chiếu.]

QUYẾT ĐỊNH

1. Về ly hôn: Chấp nhận yêu cầu ly hôn của nguyên đơn. Hôn nhân chấm dứt kể từ ngày bản án có hiệu lực.

2. Về con chung: [Giao con cho…, cấp dưỡng… – điền theo tình tiết vụ án.]

3. Về tài sản chung: [Phân chia theo thỏa thuận hoặc theo quyết định của Tòa – điền theo chứng cứ.]

4. Án phí: [Nguyên đơn/Bị đơn chịu – theo Điều 147 BLTTDS.]

Bản án có thể bị kháng cáo trong thời hạn 15 ngày kể từ ngày tuyên án.`,
}

/** Lựa chọn Kính gửi cho dropdown AI panel (phần tử đầu = mặc định khi văn bản không khớp một lựa chọn). */
const KINH_GUI_OPTIONS = [
  `- Viện kiểm sát nhân dân khu vực X – Thành Phố Hồ Chí Minh.
- Ông Nguyễn Văn Vinh; Địa chỉ: 100/23 Nguyễn Văn Văn, phường XXX, Thành Phố Hồ Chí Minh; Số điện thoại: 090xxx; là nguyên đơn.
- Bà Trần Thị Hường; Địa chỉ: 200 Nguyễn Hữu Thọ, phường YYY, Thành Phố Hồ Chí Minh; Số điện thoại: 091xxx; là bị đơn.`,
  'Viện kiểm sát nhân dân phường X, TP.HCM; Nguyên đơn; Bị đơn',
  'Viện kiểm sát nhân dân phường Thủ Thiêm, TP.HCM; Các đương sự theo hồ sơ',
]
const DEFAULT_KINH_GUI = KINH_GUI_OPTIONS[0]

/** Giá trị hiển thị trong Select — khớp cả block nhiều dòng với KINH_GUI_OPTIONS. */
function kinhGuiValueForSelect(extractedBlock: string | undefined): string {
  const t = extractedBlock?.trim() ?? ''
  const hit = KINH_GUI_OPTIONS.find((o) => o.trim() === t)
  return hit ?? DEFAULT_KINH_GUI
}

const DEFAULT_TEMPLATE = 'Thông báo thụ lý'
const initialContent = TEMPLATE_CONTENT[DEFAULT_TEMPLATE] ?? ''

/** Các cụm từ AI điền (nền vàng) theo từng biểu mẫu – phải là chuỗi con đúng trong nội dung. */
const TEMPLATE_SUGGESTIONS: Record<string, string[]> = {
  'Thông báo thụ lý': [
    'KHU VỰC X',
    'THÀNH PHỐ HỒ CHÍ MINH',
    '10',
    'ngày 10 tháng 4 năm 2026',
    'Viện kiểm sát nhân dân khu vực X – Thành Phố Hồ Chí Minh',
    'Ông Nguyễn Văn Vinh',
    '100/23 Nguyễn Văn Văn, phường XXX, Thành Phố Hồ Chí Minh',
    '090xxx',
    'Bà Trần Thị Hường',
    '200 Nguyễn Hữu Thọ, phường YYY, Thành Phố Hồ Chí Minh',
    '091xxx',
    'Ngày 10 tháng 4 năm 2026',
    '10/2026',
    '10/2026/TLST- HNGĐ',
    'HNGĐ',
    'Ly hôn',
    ' Ly hôn ',
    'Ly hôn với bà Trần Thị Hường.',
    'Yêu cầu trực tiếp nuôi dưỡng con chung là cháu Nguyễn Văn C (sinh năm 2020) và không yêu cầu cấp dưỡng.',
    'Tài sản chung, nợ chung: Tự thỏa thuận.',
    'Bản sao có chứng thực',
    'CCCD, Giấy xác nhận cư trú, Giấy khai sinh',
    'Bản chính',
    'Giấy đăng ký kết hôn',
    'VKS kv X -TP.HCM',
    'Đương sự',
    'Trần Văn Bình',
  ],
  'Giấy triệu tập': ['08 giờ 00, ngày 25 tháng 03 năm 2026', 'Phòng xử án số 3 – Tòa án nhân dân phường Bến Thành, TP.HCM'],
  'Quyết định đưa vụ án ra xét xử': ['Điều 203, 204 Bộ luật Tố tụng dân sự', 'Thẩm phán Trần Văn Bình – Tòa án nhân dân phường Bến Thành, TP.HCM'],
  'Biên bản hòa giải': ['Phòng hòa giải, Tòa án nhân dân phường Bến Thành, TP.HCM', 'Thư ký Nguyễn Thị Hương'],
  'Bản án sơ thẩm': ['Điều 51, 56 Luật Hôn nhân và gia đình 2014', 'Điều 147, 203 Bộ luật Tố tụng dân sự 2015'],
}

/** Khổ trang A4 (mm). */
const A4_WIDTH_MM = 210
const A4_HEIGHT_MM = 297
const MM_TO_PX = 3.78

type SavedDocxTemplate = {
  name: string
  base64: string
  savedAt: number
}

const SAVED_DOCX_TEMPLATES_KEY = 'aiDraft.savedDocxTemplates.v1'

function arrayBufferToBase64(buf: ArrayBuffer) {
  const bytes = new Uint8Array(buf)
  let binary = ''
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize))
  }
  return btoa(binary)
}

function base64ToArrayBuffer(base64: string) {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

export default function AIDraftPage() {
  const navigate = useNavigate()
  const [aiPanelOpen, setAiPanelOpen] = useState(true)
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE)
  const [docContent, setDocContent] = useState(initialContent)
  const [previewOpen, setPreviewOpen] = useState(false)
  /** Snapshot HTML cho Dialog (không dùng chung ref với vùng soạn — tránh ref chỉ gắn 1 node). */
  const [previewDocxHtml, setPreviewDocxHtml] = useState('')
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity?: 'success' | 'info' | 'warning' }>({ open: false, message: '' })
  const [bold, setBold] = useState(false)
  const [italic, setItalic] = useState(false)
  const [underline, setUnderline] = useState(false)
  const [align, setAlign] = useState<'left' | 'center' | 'right' | 'justify'>('left')
  const [fontSize, setFontSize] = useState(13)
  const [fontFamily, setFontFamily] = useState('Times New Roman')
  const [suggestionPhrases, setSuggestionPhrases] = useState<string[]>(() => TEMPLATE_SUGGESTIONS[DEFAULT_TEMPLATE] ?? [])
  const [aiKinhGui, setAiKinhGui] = useState(DEFAULT_KINH_GUI)
  const [aiVeViec, setAiVeViec] = useState(' Ly hôn ')
  const [docxDemoBuffer, setDocxDemoBuffer] = useState<ArrayBuffer | null>(null)
  const docxHostRef = useRef<HTMLDivElement | null>(null)
  const apiOrigin = useMemo(() => API_BASE_URL.replace(/\/backend-api\/?$/, ''), [])
  const [thuLyAi, setThuLyAi] = useState<ThuLyAiFields>(() => buildThuLyAiDefaults())
  /** Đảm bảo mọi thay đổi nested trong thuLyAi kích hoạt render DOCX + highlight. */
  const thuLyAiKey = useMemo(() => JSON.stringify(thuLyAi), [thuLyAi])
  const thuLyAiApplyRef = useRef(thuLyAi)
  thuLyAiApplyRef.current = thuLyAi
  const docxRenderGenRef = useRef(0)

  const savedDocxTemplates = useMemo(() => {
    try {
      const raw = localStorage.getItem(SAVED_DOCX_TEMPLATES_KEY)
      const parsed = raw ? (JSON.parse(raw) as SavedDocxTemplate[]) : []
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }, [snack.open]) // refresh list after save via snack toggle

  // Tạm thời giữ UI dropdown cũ: nếu người dùng chọn template DOCX đã lưu thì chỉ báo chưa hỗ trợ trong chế độ demo render docx.
  const handleLoadSavedDocxTemplate = (_name: string) => {
    setSnack({
      open: true,
      message: 'Chế độ demo hiện chỉ hiển thị template DOCX mẫu (không dùng danh sách template DOCX đã lưu).',
      severity: 'info',
    })
  }

  useEffect(() => {
    const content = TEMPLATE_CONTENT[template]
    if (content) {
      setDocxDemoBuffer(null)
      setDocContent(content)
      setSuggestionPhrases(TEMPLATE_SUGGESTIONS[template] ?? [])
      if (template === 'Thông báo thụ lý') {
        setAlign('justify')
        setThuLyAi(buildThuLyAiDefaults())
      }
      const kinhGuiBlock = content.match(/Kính gửi:\s*([\s\S]*?)\n\nNgày /)
      const veViecMatch = content.match(/về việc\s+"([^"]*)"/i)
      setAiKinhGui(kinhGuiValueForSelect(kinhGuiBlock?.[1]))
      if (veViecMatch) setAiVeViec(veViecMatch[1])
    }
  }, [template])

  const handlePreview = () => {
    if (template === 'Thông báo thụ lý' && docxHostRef.current) {
      setPreviewDocxHtml(docxHostRef.current.innerHTML)
    }
    setPreviewOpen(true)
  }

  const handleSavePrint = () => {
    setSnack({ open: true, message: 'Đã lưu bản nháp. Đang mở hộp thoại in...', severity: 'success' })
    requestAnimationFrame(() => {
      window.print()
    })
  }

  useEffect(() => {
    if (template !== 'Thông báo thụ lý') return
    void (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/docx-demo/thong-bao-thu-ly`)
        if (!res.ok) throw new Error('Fetch demo docx failed')
        const buf = await res.arrayBuffer()
        setDocxDemoBuffer(buf)
      } catch {
        setSnack({ open: true, message: 'Không tải được template DOCX demo từ backend.', severity: 'warning' })
      }
    })()
  }, [template])

  useEffect(() => {
    if (template !== 'Thông báo thụ lý') return
    if (!docxDemoBuffer || !docxHostRef.current) return
    const gen = ++docxRenderGenRef.current
    docxHostRef.current.innerHTML = ''
    void renderAsync(docxDemoBuffer, docxHostRef.current, undefined, {
      className: 'docx-preview',
      inWrapper: false,
      ignoreLastRenderedPageBreak: true,
    })
      .then(() => {
        if (gen !== docxRenderGenRef.current) return
        const host = docxHostRef.current
        if (!host) return
        host.normalize()
        applyThuLyHighlightDocx(host, thuLyAiApplyRef.current)
      })
      .catch(() => setSnack({ open: true, message: 'Không render được DOCX demo.', severity: 'warning' }))
  }, [template, docxDemoBuffer, thuLyAiKey])

  /** Áp dụng một lần: Kính gửi, Về việc (template TipTap). */
  const handleApplyAiToDocument = () => {
    if (template === 'Thông báo thụ lý') {
      // DOCX demo: thay highlight trực tiếp sau khi render
      if (docxHostRef.current) applyThuLyHighlightDocx(docxHostRef.current, thuLyAi)
      setSnack({ open: true, message: 'Đã cập nhật nội dung vùng highlight theo options.', severity: 'success' })
      return
    }
    setDocContent((prev) => {
      let next = prev.replace(/Kính gửi:\s*[\s\S]*?\n\n/, `Kính gửi:\n${aiKinhGui}\n\n`)
      next = next.replace(/về việc\s+"[^"]*"/i, `về việc "${aiVeViec}"`)
      queueMicrotask(() => {
        setSnack({ open: true, message: 'Đã áp dụng Kính gửi và “về việc”.', severity: 'success' })
      })
      return next
    })
  }

  const handleRefreshSuggestions = () => {
    setSuggestionPhrases(TEMPLATE_SUGGESTIONS[template] ?? [])
    setSnack({
      open: true,
      message: 'Đã làm mới vùng gợi ý AI (nền vàng) theo biểu mẫu hiện tại.',
      severity: 'info',
    })
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 112px)', overflow: 'hidden', boxSizing: 'border-box' }}>
      <GlobalStyles
        styles={{
          '@media print': {
            '@page': { margin: '12mm', size: 'auto' },
            body: { WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' },
            'body *': { visibility: 'hidden' },
            '#ai-draft-print-surface, #ai-draft-print-surface *': { visibility: 'visible' },
            '#ai-draft-print-surface': {
              position: 'absolute',
              left: 0,
              top: 0,
              width: '100%',
              maxWidth: '100%',
              boxShadow: 'none',
              background: '#fff',
              overflow: 'visible',
            },
          },
        }}
      />
      {/* Topbar */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          '@media print': { display: 'none' },
        }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Chọn biểu mẫu</InputLabel>
          <Select
            value={template}
            label="Chọn biểu mẫu"
            onChange={(e) => {
              const v = String(e.target.value || DEFAULT_TEMPLATE)
              if (v.startsWith('__docx_saved__:')) {
                handleLoadSavedDocxTemplate(v.replace('__docx_saved__:', ''))
                return
              }
              setTemplate(v)
            }}
          >
            <MenuItem value="">— Chọn —</MenuItem>
            {savedDocxTemplates.length > 0 && (
              <MenuItem disabled value="__docx_section__">
                — Template DOCX đã lưu —
              </MenuItem>
            )}
            {savedDocxTemplates.map((t) => (
              <MenuItem key={t.name} value={`__docx_saved__:${t.name}`}>
                {t.name}
              </MenuItem>
            ))}
            {savedDocxTemplates.length > 0 && (
              <MenuItem disabled value="__builtin_section__">
                — Biểu mẫu dựng sẵn —
              </MenuItem>
            )}
            {QUICK_TEMPLATES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" size="small" startIcon={<Visibility />} onClick={handlePreview}>Xem trước</Button>
        <Button variant="contained" size="small" startIcon={<Save />} color="primary" onClick={handleSavePrint}>Lưu & In</Button>
      </Paper>

      {template === 'Bản án sơ thẩm' && (
        <Alert severity="info" sx={{ mx: 2, mt: 1, '@media print': { display: 'none' } }} icon={<AutoAwesome />}>
          Tra cứu điều luật, án lệ tại{' '}
          <Link component="button" variant="body2" onClick={() => navigate('/workspace/ai-research')} sx={{ fontWeight: 600 }}>
            Trợ lý pháp luật
          </Link>
          {' '}để tham chiếu khi soạn bản án.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
        {/* Editor trung tâm – kiểu Word */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, maxWidth: '100%', overflow: 'hidden', bgcolor: 'grey.100' }}>
          {/* Thanh công cụ định dạng (ribbon) */}
          <Paper
            elevation={0}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 1.5, py: 0.75, '@media print': { display: 'none' } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={fontFamily}
                  onChange={(e) => setFontFamily(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ fontSize: 13, height: 32 }}
                >
                  <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                  <MenuItem value="Arial">Arial</MenuItem>
                  <MenuItem value="Calibri">Calibri</MenuItem>
                </Select>
              </FormControl>
              <Select
                size="small"
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
                sx={{ minWidth: 56, height: 32, fontSize: 13 }}
              >
                {[12, 13, 14, 16, 18, 20, 24].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <ToggleButtonGroup size="small" value={[bold && 'bold', italic && 'italic', underline && 'underline'].filter(Boolean)} sx={{ height: 32 }}>
                <ToggleButton value="bold" aria-label="Đậm" onClick={() => setBold(!bold)}>
                  <FormatBold />
                </ToggleButton>
                <ToggleButton value="italic" aria-label="Nghiêng" onClick={() => setItalic(!italic)}>
                  <FormatItalic />
                </ToggleButton>
                <ToggleButton value="underline" aria-label="Gạch chân" onClick={() => setUnderline(!underline)}>
                  <FormatUnderlined />
                </ToggleButton>
              </ToggleButtonGroup>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <ToggleButtonGroup size="small" value={align} exclusive onChange={(_, v) => v && setAlign(v)} sx={{ height: 32 }}>
                <ToggleButton value="left" aria-label="Căn trái"><FormatAlignLeft /></ToggleButton>
                <ToggleButton value="center" aria-label="Giữa"><FormatAlignCenter /></ToggleButton>
                <ToggleButton value="right" aria-label="Căn phải"><FormatAlignRight /></ToggleButton>
                <ToggleButton value="justify" aria-label="Đều"><FormatAlignJustify /></ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Paper>
          {/* Ruler giả lập (vạch cm) */}
          <Box
            sx={{
              px: 2,
              py: 0.5,
              bgcolor: 'grey.200',
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'flex-end',
              gap: 0,
              minHeight: 24,
              '@media print': { display: 'none' },
            }}
          >
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map((i) => (
              <Box key={i} sx={{ width: A4_WIDTH_MM * MM_TO_PX / 20, textAlign: 'center', fontSize: 10, color: 'text.secondary' }}>
                {i % 5 === 0 ? i : ''}
              </Box>
            ))}
          </Box>
          {/* Vùng trang A4 */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'auto',
              py: 2,
              px: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              '@media print': { overflow: 'visible', py: 0, px: 0 },
            }}
          >
            <Paper
              id="ai-draft-print-surface"
              elevation={2}
              sx={{
                width: '100%',
                maxWidth: A4_WIDTH_MM * MM_TO_PX,
                minWidth: 0,
                minHeight: A4_HEIGHT_MM * MM_TO_PX,
                bgcolor: '#fff',
                p: 3,
                boxSizing: 'border-box',
                overflow: 'hidden',
                '@media print': { boxShadow: 'none', minHeight: 'auto', p: 2 },
                '& .ai-suggestion': {
                  backgroundColor: 'rgba(255, 235, 59, 0.35)',
                  padding: '0 2px',
                  borderRadius: 1,
                },
                '& .docx-preview': {
                  maxWidth: '100%',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  boxSizing: 'border-box',
                  /** Cỡ cơ sở ~13px; không dùng selector * để không ghi đè run/heading trong file Word. */
                  fontSize: '13px',
                  lineHeight: 1.6,
                  fontFamily: '"Times New Roman", Times, serif',
                },
                '& .docx-preview table': {
                  maxWidth: '100%',
                  tableLayout: 'fixed',
                  wordBreak: 'break-word',
                },
                '& .docx-preview img': {
                  maxWidth: '100%',
                  height: 'auto',
                },
                /** Dòng địa danh: ghi đè canh phải/italic từ Word (inline style). */
                '& [data-dia-danh-nudge="1"]': {
                  textAlign: 'center !important',
                  width: '100%',
                  boxSizing: 'border-box',
                },
                '& [data-dia-danh-nudge="1"] *': {
                  fontStyle: 'normal',
                },
              }}
            >
              {template === 'Thông báo thụ lý' ? (
                <Box ref={docxHostRef} sx={{ width: '100%', minWidth: 0, overflowX: 'auto' }} />
              ) : (
                <>
                  <LegalDraftTiptapEditor
                    value={docContent}
                    onChange={setDocContent}
                    suggestionPhrases={suggestionPhrases}
                    onAcceptPhrase={(phrase) => {
                      setSuggestionPhrases((prev) => prev.filter((p) => p !== phrase))
                      setSnack({ open: true, message: 'Đã chấp nhận gợi ý.', severity: 'success' })
                    }}
                    onDismissPhrase={(phrase) => {
                      setSuggestionPhrases((prev) => prev.filter((p) => p !== phrase))
                      setSnack({ open: true, message: 'Đã bỏ qua gợi ý.', severity: 'info' })
                    }}
                    fontFamily={fontFamily}
                    fontSize={fontSize}
                    bold={bold}
                    italic={italic}
                    underline={underline}
                    textAlign={align}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                    Các trường AI điền có nền vàng nhạt. Nhấn <strong>Tab</strong> để chấp nhận gợi ý, <strong>Esc</strong> để bỏ qua.
                  </Typography>
                </>
              )}
            </Paper>
          </Box>
        </Box>

        {/* AI Panel phải (320px) — nội dung dài: scroll trong panel */}
        <Box sx={{ display: 'flex', flexShrink: 0, alignSelf: 'stretch', minHeight: 0, '@media print': { display: 'none' } }}>
          <Paper
            elevation={0}
            sx={{
              width: aiPanelOpen ? 320 : 0,
              borderLeft: 1,
              borderColor: 'divider',
              overflow: 'hidden',
              transition: 'width 0.2s',
              height: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
          <Collapse in={aiPanelOpen} orientation="horizontal" sx={{ height: '100%', minHeight: 0, display: 'flex' }}>
            <Box
              sx={{
                width: 320,
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 2, pt: 2, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" fontWeight={600}>✨ AI Tự điền & Gợi ý động</Typography>
                <IconButton size="small" onClick={() => setAiPanelOpen(false)}><ChevronRight /></IconButton>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  px: 2,
                  pb: 1,
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                {template === 'Thông báo thụ lý' ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      1. Khu vực
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      label="Nội dung"
                      value={thuLyAi.khuVuc}
                      onChange={(e) => setThuLyAi((p) => ({ ...p, khuVuc: e.target.value }))}
                    />
                    <Typography variant="subtitle2" fontWeight={700}>
                      2. Thành phố
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      label="Nội dung"
                      value={thuLyAi.thanhPho}
                      onChange={(e) => setThuLyAi((p) => ({ ...p, thanhPho: e.target.value }))}
                    />
                    <Typography variant="subtitle2" fontWeight={700}>
                      3. Số thông báo
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      label="Nội dung"
                      value={thuLyAi.soThongBao}
                      onChange={(e) => setThuLyAi((p) => ({ ...p, soThongBao: e.target.value }))}
                    />
                    <Typography variant="subtitle2" fontWeight={700}>
                      4. Địa danh, thời gian
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      label="Nội dung"
                      value={thuLyAi.diaDanhNgayThang}
                      onChange={(e) => setThuLyAi((p) => ({ ...p, diaDanhNgayThang: e.target.value }))}
                    />

                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>5. Kính gửi</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Viện kiểm sát nhân dân {thuLyAi.khuVuc} – {thuLyAi.thanhPho}
                    </Typography>

                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>6. Nguyên đơn</Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField size="small" label="Xưng hô" value={thuLyAi.nguyenDon.xungHo} onChange={(e) => setThuLyAi((p) => ({ ...p, nguyenDon: { ...p.nguyenDon, xungHo: e.target.value } }))} sx={{ width: 96 }} />
                      <TextField size="small" fullWidth label="Họ tên" value={thuLyAi.nguyenDon.hoTen} onChange={(e) => setThuLyAi((p) => ({ ...p, nguyenDon: { ...p.nguyenDon, hoTen: e.target.value } }))} />
                    </Stack>
                    <TextField size="small" fullWidth label="Địa chỉ" value={thuLyAi.nguyenDon.diaChi} onChange={(e) => setThuLyAi((p) => ({ ...p, nguyenDon: { ...p.nguyenDon, diaChi: e.target.value } }))} />
                    <TextField size="small" fullWidth label="Điện thoại" value={thuLyAi.nguyenDon.dienThoai} onChange={(e) => setThuLyAi((p) => ({ ...p, nguyenDon: { ...p.nguyenDon, dienThoai: e.target.value } }))} />

                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>7. Bị đơn</Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField size="small" label="Xưng hô" value={thuLyAi.biDon.xungHo} onChange={(e) => setThuLyAi((p) => ({ ...p, biDon: { ...p.biDon, xungHo: e.target.value } }))} sx={{ width: 96 }} />
                      <TextField size="small" fullWidth label="Họ tên" value={thuLyAi.biDon.hoTen} onChange={(e) => setThuLyAi((p) => ({ ...p, biDon: { ...p.biDon, hoTen: e.target.value } }))} />
                    </Stack>
                    <TextField size="small" fullWidth label="Địa chỉ" value={thuLyAi.biDon.diaChi} onChange={(e) => setThuLyAi((p) => ({ ...p, biDon: { ...p.biDon, diaChi: e.target.value } }))} />
                    <TextField size="small" fullWidth label="Điện thoại" value={thuLyAi.biDon.dienThoai} onChange={(e) => setThuLyAi((p) => ({ ...p, biDon: { ...p.biDon, dienThoai: e.target.value } }))} />

                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>
                      8. Ngày thụ lý
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      label="Nội dung"
                      value={thuLyAi.ngayThuLy}
                      onChange={(e) => setThuLyAi((p) => ({ ...p, ngayThuLy: e.target.value }))}
                    />
                    <Typography variant="subtitle2" fontWeight={700}>
                      9. Số vụ án
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      label="Nội dung"
                      value={thuLyAi.soVuAn}
                      onChange={(e) => setThuLyAi((p) => ({ ...p, soVuAn: e.target.value }))}
                    />
                    <Typography variant="subtitle2" fontWeight={700}>
                      10. Về việc
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      label="Nội dung"
                      value={thuLyAi.veViec}
                      onChange={(e) => setThuLyAi((p) => ({ ...p, veViec: e.target.value }))}
                    />

                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>
                      11. Người khởi kiện
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      label="Nội dung"
                      value={thuLyAi.nguoiKhoiKien}
                      onChange={(e) => setThuLyAi((p) => ({ ...p, nguoiKhoiKien: e.target.value }))}
                    />
                    <Typography variant="subtitle2" fontWeight={700}>
                      12. Địa chỉ người khởi kiện
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      label="Nội dung"
                      value={thuLyAi.diaChiNguoiKhoiKien}
                      onChange={(e) => setThuLyAi((p) => ({ ...p, diaChiNguoiKhoiKien: e.target.value }))}
                    />
                    <Typography variant="subtitle2" fontWeight={700}>
                      13. Điện thoại người khởi kiện
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      label="Nội dung"
                      value={thuLyAi.dienThoaiNguoiKhoiKien}
                      onChange={(e) => setThuLyAi((p) => ({ ...p, dienThoaiNguoiKhoiKien: e.target.value }))}
                    />

                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>14. Những vấn đề yêu cầu Toà án giải quyết</Typography>
                    <Stack spacing={1}>
                      {thuLyAi.vanDeYeuCau.map((row, idx) => (
                        <Stack key={idx} direction="row" spacing={1} alignItems="center">
                          <TextField size="small" fullWidth label={`Dòng ${idx + 1}`} value={row} onChange={(e) => setThuLyAi((p) => ({ ...p, vanDeYeuCau: p.vanDeYeuCau.map((r, i) => (i === idx ? e.target.value : r)) }))} />
                          <Button size="small" variant="outlined" onClick={() => setThuLyAi((p) => ({ ...p, vanDeYeuCau: p.vanDeYeuCau.length > 1 ? p.vanDeYeuCau.filter((_, i) => i !== idx) : p.vanDeYeuCau }))}>Xóa</Button>
                        </Stack>
                      ))}
                      <Button size="small" variant="outlined" onClick={() => setThuLyAi((p) => ({ ...p, vanDeYeuCau: [...p.vanDeYeuCau, ''] }))}>Thêm dòng</Button>
                    </Stack>

                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>15. Các tài liệu, chứng cứ đã nộp</Typography>
                    <Stack spacing={1}>
                      {thuLyAi.taiLieuChungCu.map((row, idx) => (
                        <Stack key={idx} direction="row" spacing={1} alignItems="center">
                          <TextField size="small" fullWidth label={`Dòng ${idx + 1}`} value={row} onChange={(e) => setThuLyAi((p) => ({ ...p, taiLieuChungCu: p.taiLieuChungCu.map((r, i) => (i === idx ? e.target.value : r)) }))} />
                          <Button size="small" variant="outlined" onClick={() => setThuLyAi((p) => ({ ...p, taiLieuChungCu: p.taiLieuChungCu.length > 1 ? p.taiLieuChungCu.filter((_, i) => i !== idx) : p.taiLieuChungCu }))}>Xóa</Button>
                        </Stack>
                      ))}
                      <Button size="small" variant="outlined" onClick={() => setThuLyAi((p) => ({ ...p, taiLieuChungCu: [...p.taiLieuChungCu, ''] }))}>Thêm dòng</Button>
                    </Stack>

                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>16. Viện kiểm sát nhân dân</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Viện kiểm sát nhân dân {thuLyAi.khuVuc} – {thuLyAi.thanhPho}
                    </Typography>

                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>17. Nơi nhận</Typography>
                    <Stack spacing={1}>
                      {thuLyAi.noiNhan.map((row, idx) => (
                        <Stack key={idx} direction="row" spacing={1} alignItems="center">
                          <TextField size="small" fullWidth label={`Dòng ${idx + 1}`} value={row} onChange={(e) => setThuLyAi((p) => ({ ...p, noiNhan: p.noiNhan.map((r, i) => (i === idx ? e.target.value : r)) }))} />
                          <Button size="small" variant="outlined" onClick={() => setThuLyAi((p) => ({ ...p, noiNhan: p.noiNhan.length > 1 ? p.noiNhan.filter((_, i) => i !== idx) : p.noiNhan }))}>Xóa</Button>
                        </Stack>
                      ))}
                      <Button size="small" variant="outlined" onClick={() => setThuLyAi((p) => ({ ...p, noiNhan: [...p.noiNhan, ''] }))}>Thêm dòng</Button>
                    </Stack>

                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>
                      18. Thẩm phán
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      label="Nội dung"
                      value={thuLyAi.thamPhan}
                      onChange={(e) => setThuLyAi((p) => ({ ...p, thamPhan: e.target.value }))}
                    />
                  </Box>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary">Kính gửi:</Typography>
                    <Select
                      size="small"
                      fullWidth
                      value={aiKinhGui}
                      onChange={(e) => setAiKinhGui(e.target.value)}
                      sx={{ mt: 0.5 }}
                    >
                      {KINH_GUI_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt} sx={{ whiteSpace: 'normal' }}>{opt}</MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Về việc:</Typography>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      value={aiVeViec}
                      onChange={(e) => setAiVeViec(e.target.value)}
                      placeholder="Tranh chấp hôn nhân và gia đình"
                      sx={{ mt: 0.5 }}
                    />
                  </>
                )}
                <Button size="small" variant="contained" fullWidth sx={{ mt: 1.5 }} onClick={handleApplyAiToDocument}>
                  Áp dụng vào văn bản
                </Button>
              </Box>
              <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1, bgcolor: 'background.paper' }}>
                <Button size="small" startIcon={<Refresh />} onClick={handleRefreshSuggestions}>Làm mới gợi ý (vùng vàng)</Button>
                <Button size="small" startIcon={<Assignment />} onClick={() => { setDocContent(TEMPLATE_CONTENT[template] ?? docContent); setSuggestionPhrases(TEMPLATE_SUGGESTIONS[template] ?? []); setSnack({ open: true, message: 'Đã nạp lại nội dung biểu mẫu.', severity: 'success' }); }}>Dùng toàn bộ template</Button>
              </Box>
            </Box>
          </Collapse>
          </Paper>
          {!aiPanelOpen && (
            <Box sx={{ width: 28, borderLeft: 1, borderColor: 'divider', display: 'flex', alignItems: 'flex-start', pt: 2, '@media print': { display: 'none' } }}>
              <IconButton size="small" onClick={() => setAiPanelOpen(true)}><ChevronRight /></IconButton>
            </Box>
          )}
        </Box>
      </Box>

      {/* Dialog Xem trước */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: '80vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Xem trước – {template}</span>
          <Box>
            <Button
              size="small"
              startIcon={<Print />}
              onClick={() => {
                setSnack({ open: true, message: 'Đang mở hộp thoại in...', severity: 'info' })
                setPreviewOpen(false)
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => window.print())
                })
              }}
            >
              In
            </Button>
            <Button size="small" onClick={() => setPreviewOpen(false)}>Đóng</Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Paper variant="outlined" sx={{ p: 3, bgcolor: '#fff', width: '100%', maxWidth: A4_WIDTH_MM * MM_TO_PX, minWidth: 0, mx: 'auto', boxSizing: 'border-box', overflow: 'hidden' }}>
            {template === 'Thông báo thụ lý' ? (
              <Box
                sx={{
                  width: '100%',
                  minWidth: 0,
                  overflowX: 'auto',
                  '& .docx-preview': {
                    maxWidth: '100%',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    fontSize: '13px',
                    lineHeight: 1.6,
                    fontFamily: '"Times New Roman", Times, serif',
                  },
                }}
                dangerouslySetInnerHTML={{ __html: previewDocxHtml }}
              />
            ) : (
              <Typography component="pre" sx={{ fontFamily: 'Times New Roman, serif', fontSize: 13, lineHeight: 1.8, whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                {docContent}
              </Typography>
            )}
          </Paper>
        </DialogContent>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity || 'info'} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
